/**
 * Pure CPS/WPM math for the /guides/subtitle-reading-speed/ widget
 * (cue-forge-kjc.3). This module ships to the browser as-is — it is the only
 * import `readingSpeed.client.ts` makes — so it imports NOTHING from
 * `src/core`. Every code-point range (the invisible bidi control marks, a
 * preset's own `excludeRanges`) is passed in as plain data by the build-time
 * caller (`content/cpsThresholds.ts`), which is the only place allowed to read
 * the real presets, and is embedded on the page as JSON.
 *
 * The counting rule mirrors `src/core/qc/rules/cps.ts` `maxCps.evaluate`
 * exactly: count Unicode code points via `for...of`, skip anything in
 * `excludeRanges`, and add back `lines.length - 1` as line-break characters
 * when `excludeLineBreaks` is false. `readingSpeed.test.ts` asserts this with
 * an engine-parity test, so the widget can never disagree with the rule it
 * describes.
 */

/** Inclusive Unicode code-point range `[low, high]`. */
export type CodePointRange = readonly [number, number];

/** Per-preset config the widget checks a cue against, embedded as page JSON. */
export interface CpsThresholdConfig {
  id: string;
  label: string;
  maxCps: number;
  excludeLineBreaks: boolean;
  /** Always includes the bidi control ranges merged in on top of the preset's own. */
  excludeRanges: CodePointRange[];
  minDurationMs: number;
  maxDurationMs: number;
}

/** The full JSON payload embedded in the page's `#cps-thresholds` block. */
export interface CpsWidgetData {
  /** The invisible bidi control marks, always excluded, independent of preset. */
  bidiRanges: CodePointRange[];
  presets: CpsThresholdConfig[];
}

/** Pass/over verdict for one preset against the given input. */
export interface PresetVerdict {
  id: string;
  label: string;
  cps: number;
  maxCps: number;
  pass: boolean;
}

/** Everything the widget renders for one text + duration input. */
export interface ReadingSpeedResult {
  /** Counted characters excluding line breaks and bidi marks only (preset-neutral). */
  chars: number;
  cps: number;
  wpm: number;
  verdicts: PresetVerdict[];
}

function inRanges(codePoint: number, ranges: readonly CodePointRange[]): boolean {
  for (const [lo, hi] of ranges) {
    if (codePoint >= lo && codePoint <= hi) return true;
  }
  return false;
}

/** Counted code points in one line of text, skipping anything in `excludeRanges`. */
export function countChars(text: string, excludeRanges: readonly CodePointRange[] = []): number {
  let n = 0;
  for (const ch of text) {
    const cp = ch.codePointAt(0);
    if (cp !== undefined && inRanges(cp, excludeRanges)) continue;
    n++;
  }
  return n;
}

/**
 * Counted characters across a cue's lines (already split on `\n`), mirroring
 * `cueChars` in `src/core/qc/rules/cps.ts`: sum each line's counted
 * characters, then add `lines.length - 1` as line-break characters when
 * `excludeLineBreaks` is false.
 */
export function countCueChars(
  lines: readonly string[],
  excludeLineBreaks: boolean,
  excludeRanges: readonly CodePointRange[] = [],
): number {
  let chars = lines.reduce((sum, line) => sum + countChars(line, excludeRanges), 0);
  if (!excludeLineBreaks) chars += Math.max(0, lines.length - 1);
  return chars;
}

/**
 * Characters per second. A non-positive duration returns 0 rather than
 * dividing by zero — the same case the QC engine's `max-cps` rule skips
 * entirely instead of flagging (timing rules catch a zero/negative duration
 * separately).
 */
export function computeCps(chars: number, seconds: number): number {
  if (!(seconds > 0)) return 0;
  return chars / seconds;
}

/**
 * Words per minute — a widget-only reference figure. No QC preset checks WPM;
 * every preset checks CPS. Word count is a plain whitespace split.
 */
export function computeWpm(text: string, seconds: number): number {
  if (!(seconds > 0)) return 0;
  const words = text.trim().length === 0 ? 0 : text.trim().split(/\s+/).length;
  return (words / seconds) * 60;
}

/** Pass/over verdict for one preset config against the given cue lines + duration. */
export function verdictFor(
  config: CpsThresholdConfig,
  lines: readonly string[],
  seconds: number,
): PresetVerdict {
  const chars = countCueChars(lines, config.excludeLineBreaks, config.excludeRanges);
  const cps = computeCps(chars, seconds);
  return {
    id: config.id,
    label: config.label,
    cps,
    maxCps: config.maxCps,
    // A non-positive duration has nothing to flag (the engine skips it too),
    // so there is no violation to report — the widget shows it as "pass".
    pass: !(seconds > 0) || cps <= config.maxCps,
  };
}

/**
 * Full widget computation for one input: text (possibly multi-line) plus a
 * duration in seconds. The headline `chars`/`cps` figure excludes line breaks
 * and the always-on bidi control marks only — no preset-specific range — so it
 * reads as a neutral "how fast is this" number; each preset's own verdict
 * recomputes with that preset's own `excludeRanges` (e.g. Arabic diacritics).
 */
export function evaluateReadingSpeed(
  text: string,
  seconds: number,
  data: CpsWidgetData,
): ReadingSpeedResult {
  const lines = text.split('\n');
  const chars = countCueChars(lines, true, data.bidiRanges);
  return {
    chars,
    cps: computeCps(chars, seconds),
    wpm: computeWpm(text, seconds),
    verdicts: data.presets.map((c) => verdictFor(c, lines, seconds)),
  };
}
